import { IssueFieldEnum } from "@mcapps/shared/enums";
import { Badge, Button, Card, CloseButton, Flex, Group, TextInput, Tooltip } from "@mantine/core";
import { IconCircle, IconCircleCheck, IconFilter, IconSparkles } from "@tabler/icons-react";
import type { AgGridReact } from "ag-grid-react";
import { useCallback, useMemo, type RefObject } from "react";
import { useSelector } from "react-redux";
import { useAppDispatch, useAppSelector } from "../../../../../hooks";
import {
  selectActiveIssueTableColumnFilterCount,
  selectIssueRegisterNameOptionOnBoard,
  selectNrOfFiltredIssues,
  selectTaskRegisterNameOptionOnBoard,
} from "../../issueSelectors";
import { selectIssueAskAiFilterOnCurrentBoard, selectIssueAskAiPanelOpen } from "../../issueAskAiSelectors";
import {
  setIssueDropdownSingleDataFieldFilterInRedux,
  setIssueAskAiPanelOpenInRedux,
  setMiniSearchFreeTextInRedux,
  setTaskAndOrIssueArgsInRedux,
} from "../../issueSlice";
import { clearIssueTableColumnFiltersThunk } from "../../issueThunk";
import { FilterDropdown } from "./FilterDropdown";
import {
  makeSelectActiveFiltersByField,
  makeSelectUniqueIssueFilterFromDataField,
  selectAssignedToHashmap,
  selectDisciplineFilter,
  selectIssueDatesFiler,
  selectMineSakerFilter,
} from "./issueFilterSelectors";

const priortyMap = { LOW: "Lav", MEDIUM: "Medium", HIGH: "Høy" };

type IssueDbFilterBarNewProps = {
  gridRef: RefObject<AgGridReact>;
};

export const IssueDbFilterBarNew: React.FC<IssueDbFilterBarNewProps> = ({ gridRef }) => {
  const dispatch = useAppDispatch();
  const selectFilters = useMemo(makeSelectUniqueIssueFilterFromDataField, []);
  const selectActive = useMemo(makeSelectActiveFiltersByField, []);

  const tagsFilters = useAppSelector((state) => selectFilters(state, IssueFieldEnum.TAGS, null));
  const priorityFilters = useAppSelector((state) => selectFilters(state, IssueFieldEnum.PRIORITY, priortyMap));
  const statusFilters = useAppSelector((state) => selectFilters(state, IssueFieldEnum.STATUS, null));
  const typeFilters = useAppSelector((state) => selectFilters(state, IssueFieldEnum.TYPEOFISSUE, null));
  const bucketFilters = useAppSelector((state) => selectFilters(state, IssueFieldEnum.BUCKET, null));
  const disciplineFilter = useSelector(selectDisciplineFilter);
  const assignedToHashmap = useAppSelector(selectAssignedToHashmap);
  const assignedToFilters = useAppSelector((state) =>
    selectFilters(state, IssueFieldEnum.ASSIGNED_TO_ID, assignedToHashmap),
  );
  const mineSakerFilter = useAppSelector(selectMineSakerFilter);
  const dueDateFilter = useAppSelector(selectIssueDatesFiler);

  const activeTags = useAppSelector((state) => selectActive(state, IssueFieldEnum.TAGS));
  const activePriority = useAppSelector((state) => selectActive(state, IssueFieldEnum.PRIORITY));
  const activeStatus = useAppSelector((state) => selectActive(state, IssueFieldEnum.STATUS));
  const activeAssignedTo = useAppSelector((state) => selectActive(state, IssueFieldEnum.ASSIGNED_TO_ID));
  const activeDueDate = useAppSelector((state) => selectActive(state, IssueFieldEnum.DATES_DUE));
  const activeDiscipline = useAppSelector((state) => selectActive(state, IssueFieldEnum.DISCIPLIN));
  const activeType = useAppSelector((state) => selectActive(state, IssueFieldEnum.TYPEOFISSUE));
  const activeBucket = useAppSelector((state) => selectActive(state, IssueFieldEnum.BUCKET));
  const activeMineSaker = useAppSelector((state) => selectActive(state, IssueFieldEnum.MINE_SAKER));
  const nrOfFilteredIssues = useAppSelector(selectNrOfFiltredIssues);
  const activeTableColumnFilterCount = useAppSelector(selectActiveIssueTableColumnFilterCount);
  const miniSearchFreeText = useAppSelector((state) => state.issueReducerNew.miniSearchFreeText);

  const filterConfigs = useMemo(
    () =>
      [
        { filter: mineSakerFilter, active: activeMineSaker, field: IssueFieldEnum.MINE_SAKER },
        { filter: statusFilters, active: activeStatus, field: IssueFieldEnum.STATUS },
        { filter: dueDateFilter, active: activeDueDate, field: IssueFieldEnum.DATES_DUE },
        { filter: bucketFilters, active: activeBucket, field: IssueFieldEnum.BUCKET },
        { filter: tagsFilters, active: activeTags, field: IssueFieldEnum.TAGS },
        { filter: typeFilters, active: activeType, field: IssueFieldEnum.TYPEOFISSUE },
        { filter: assignedToFilters, active: activeAssignedTo, field: IssueFieldEnum.ASSIGNED_TO_ID },
        { filter: disciplineFilter, active: activeDiscipline, field: IssueFieldEnum.DISCIPLIN },
        { filter: priorityFilters, active: activePriority, field: IssueFieldEnum.PRIORITY },
      ].filter((config) => config.filter?.options != null),
    [
      mineSakerFilter,
      statusFilters,
      dueDateFilter,
      bucketFilters,
      tagsFilters,
      typeFilters,
      assignedToFilters,
      disciplineFilter,
      priorityFilters,
      activeMineSaker,
      activeStatus,
      activeDueDate,
      activeBucket,
      activeTags,
      activeType,
      activeAssignedTo,
      activeDiscipline,
      activePriority,
    ],
  );

  const handleFilterChange = useCallback(
    (filterKey: IssueFieldEnum, values: string[]) => {
      dispatch(setIssueDropdownSingleDataFieldFilterInRedux({ filter: filterKey, values }));
    },
    [dispatch],
  );
  const clearActiveTableColumnFilters = useCallback(() => {
    gridRef.current?.api?.setFilterModel(null);
    dispatch(clearIssueTableColumnFiltersThunk());
  }, [dispatch, gridRef]);

  return (
    <Card mx="xxs" p="xs" withBorder radius="sm" bg="#fafafb" bd="1px solid var(--mantine-color-warmgray-2)">
      <Group justify="space-between" wrap="nowrap" gap="0">
        <Flex rowGap="xxs" columnGap="0.7em" wrap="wrap" align="center">
          <TaskIssueToggle />
          <TextInput
            placeholder="Søk..."
            value={miniSearchFreeText}
            onChange={(event) => dispatch(setMiniSearchFreeTextInRedux(event.currentTarget.value))}
            size="sm"
            w={150}
            miw={120}
            rightSection={
              miniSearchFreeText?.length > 0 && (
                <CloseButton size="sm" onClick={() => dispatch(setMiniSearchFreeTextInRedux(""))} title="Tøm søk" />
              )
            }
          />
          {filterConfigs.map(({ filter, active, field }) => (
            <FilterDropdown
              key={field}
              filter={filter}
              value={active}
              onChange={(key, values) => handleFilterChange(key as IssueFieldEnum, values)}
              enableSearch
            />
          ))}
        </Flex>
        <Group miw="40px" justify="end" align="center" gap="xs" wrap="nowrap">
          <IssueAskAiFilterButton />
          {activeTableColumnFilterCount > 0 && (
            <Group gap={2} wrap="nowrap">
              <Tooltip label={`${activeTableColumnFilterCount} aktive tabellfiltere`}>
                <Badge
                  aria-label={`${activeTableColumnFilterCount} aktive tabellfiltere`}
                  color="yellow"
                  leftSection={<IconFilter size={12} />}
                  size="sm"
                  variant="light"
                  tt="none"
                >
                  {activeTableColumnFilterCount}
                </Badge>
              </Tooltip>
              <Tooltip label="Fjern tabellfiltere">
                <CloseButton
                  size="sm"
                  onClick={clearActiveTableColumnFilters}
                  title="Fjern tabellfiltere"
                  aria-label="Fjern tabellfiltere"
                />
              </Tooltip>
            </Group>
          )}
          <Badge color="green" size="sm" variant="filled">
            {nrOfFilteredIssues}
          </Badge>
        </Group>
      </Group>
    </Card>
  );
};

const IssueAskAiFilterButton: React.FC = () => {
  const dispatch = useAppDispatch();
  const activeFilter = useAppSelector(selectIssueAskAiFilterOnCurrentBoard);
  const isPanelOpen = useAppSelector(selectIssueAskAiPanelOpen);
  const openPanel = () => dispatch(setIssueAskAiPanelOpenInRedux(true));

  if (activeFilter != null) {
    return (
      <Tooltip label={activeFilter.question} multiline maw={320} disabled={activeFilter.question.trim().length === 0}>
        <Button
          size="sm"
          px="xs"
          color="violet"
          variant={isPanelOpen ? "filled" : "light"}
          leftSection={<IconSparkles size={16} />}
          onClick={openPanel}
        >
          AI-filter
        </Button>
      </Tooltip>
    );
  }

  return (
    <Button
      size="sm"
      px="xs"
      color="violet"
      variant="light"
      leftSection={<IconSparkles size={16} />}
      onClick={openPanel}
    >
      Spør AI (beta)
    </Button>
  );
};

export const TaskIssueToggle: React.FC = () => {
  const dispatch = useAppDispatch();
  const selected = useAppSelector((state) => state.issueReducerNew.taskAndOrIssueArgs);
  const isTask = selected.includes("TASK");
  const isIssue = selected.includes("ISSUE");
  const issueNameOption = useAppSelector(selectIssueRegisterNameOptionOnBoard);
  const taskNameOption = useAppSelector(selectTaskRegisterNameOptionOnBoard);
  const update = useCallback(
    (type: "TASK" | "ISSUE") => {
      let next = selected.includes(type) ? selected.filter((item) => item !== type) : [...selected, type];
      if (next.length === 0) next = ["TASK", "ISSUE"];
      dispatch(setTaskAndOrIssueArgsInRedux(next));
    },
    [dispatch, selected],
  );

  return (
    <Group gap="2px" wrap="nowrap">
      <Button
        style={{ borderRadius: "6px 0 0 6px" }}
        size="sm"
        px="xs"
        color={isIssue ? "mc-primary" : "warmgray.6"}
        variant={isIssue ? "filled" : "light"}
        onClick={() => update("ISSUE")}
        rightSection={isIssue ? <IconCircleCheck size={18} /> : <IconCircle size={18} />}
      >
        {issueNameOption ? issueNameOption.singular : "Sak"}
      </Button>
      <Button
        style={{ borderRadius: "0 6px 6px 0" }}
        size="sm"
        px="xs"
        color={isTask ? "mc-primary" : "warmgray.6"}
        variant={isTask ? "filled" : "light"}
        onClick={() => update("TASK")}
        rightSection={isTask ? <IconCircleCheck size={18} /> : <IconCircle size={18} />}
      >
        {taskNameOption ? taskNameOption.singular : "Aksjon"}
      </Button>
    </Group>
  );
};
